// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SavePopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//</copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from "react";
import { remove, isEqual, sortBy } from "lodash/fp";

import connect from "share/enhancers/Izenda.Connect";
import Popup from "share/components/controls/CommonPopup/Izenda.Popup";
import DropDown from "share/components/controls/Izenda.DropDown";
import Select from "share/components/controls/multiSelectBox/Izenda.Select";
import Label from "share/components/controls/Izenda.Label";
import Constants from "logic/data/Izenda.ReportDesignerConstants";
import GridLayout from "share/components/controls/Izenda.GridLayout";
import Validator from "share/components/hoc/Izenda.Validator";
import LicenseCheckerConstants from "logic/data/Izenda.LicenseCheckerConstants";
import action from "./Izenda.SavePopupAction";
import Loading from "share/components/controls/Izenda.Loading";

import Input from "share/components/controls/Izenda.Input";
const ValidatorInput = Validator()(Input);

@connect(action)
class SavePopup extends Component {
  static propTypes = {};

  componentDidMount() {
    const { store, reportName, setReportName, updateSaveInto } = this.props;
    store.loadCategorySettings();
    if (store.isNewReport) {
      const isReportType =
        store.model.reportType === Constants.REPORT_TYPE_ENUM.REPORT;
      const reportDefaultName = isReportType
        ? Constants.DEFAULT_REPORT_NAME
        : Constants.DEFAULT_TEMPLATE_NAME;
      if (reportName === Constants.DEFAULT_REPORT_NAME) {
        setReportName(reportDefaultName);
      }
    } else {
      // The default value of SaveInto depends on Global Report checkbox.
      // But when "save as" an exist report, it's default to local category
      updateSaveInto(Constants.GLOBAL_REPORT_ENUM.LOCAL);
    }
  }

  onReportNameChanged = (event: any) => {
    this.props.setReportName(event.target.value);
  };

  onReportTypeChanged = (event: any) => {
    this.props.changeReportType(event.value);
  };

  onSaveIntoChanged = (event: any) => {
    this.props.updateSaveInto(event.value);
  };

  onCategoryChanged = (id: string, name: string) => {
    this.props.changeCategory(id, name);
  };

  onSubCategoryChanged = (id: string, name: string) => {
    this.props.changeSubCategory(id, name);
  };

  renderContainer() {
    const {
      localization,
      authorization,
      store,
      authorizationConstants,
      addNewCategory,
      addNewSubCategory,
      reportName,
      saveInto
    } = this.props;
    const model = store.model;
    let types = model.types;
    const hasLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportTemplateComponent);
    if (!hasLicense) {
      types = remove(type => isEqual(type.value, "Template"), types);
    }

    const categories = sortBy(cat => cat.name.toLowerCase(), model.categories);
    const subCategories = sortBy(
      cat => cat.name.toLowerCase(),
      model.subCategories
    );
    const isReportType = model.reportType === Constants.REPORT_TYPE_ENUM.REPORT;

    const hasPrivilege = authorization.checkPrivileges([
      authorizationConstants.PERMISSION_KEY.ReportsCanCreateNewCategoryValue
    ]);

    let onAddNewCategory = addNewCategory;
    let noResultsTextCategory = Constants.HINT_MESSAGE.CATEGORY;
    let onAddNewSubCategory = addNewSubCategory;
    let noResultsTextSubCategory = Constants.HINT_MESSAGE.SUB_CATEGORY;
    if (!hasPrivilege) {
      onAddNewCategory = false;
      noResultsTextCategory = null;
      onAddNewSubCategory = false;
      noResultsTextSubCategory = null;
    }

    const reportNameVals = isReportType
      ? Constants.CONSTRAINTS.REPORT_NAME
      : Constants.CONSTRAINTS.TEMPLATE_NAME;
    const statusSaveInto = store.statusSaveInto;

    if (!store.globalReportName || !store.localReportName) {
      return <Loading show />;
    }
    const GLOBAL_REPORT = [
      {
        value: Constants.GLOBAL_REPORT_ENUM.LOCAL,
        label: store.localReportName
      },
      {
        value: Constants.GLOBAL_REPORT_ENUM.GLOBAL,
        label: store.globalReportName
      }
    ];

    return (
      <GridLayout className="form-horizontal">
        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label u_textNormal">
              {isReportType
                ? localization.getText("REPORTDESIGNER_LABEL_REPORT_NAME")
                : localization.getText("REPORTDESIGNER_LABEL_TEMPLATE_NAME")}
            </Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <ValidatorInput
              value={reportName}
              className="form-control"
              onChange={this.onReportNameChanged}
              validations={reportNameVals}
              field="name"
              type="text"
              name="reportName"
            />
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label u_textNormal">
              {localization.getText("COMMON_LABEL_SAVE_AS")}
            </Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <DropDown
              options={types}
              onChange={this.onReportTypeChanged}
              value={store.model.reportType}
              valueField="id"
              labelField="name"
              disabled={store.disableReportType}
            />
          </GridLayout.Col>
        </GridLayout.Row>
        {statusSaveInto.visible && (
          <GridLayout.Row>
            <GridLayout.Col cols="3">
              <Label className="control-label u_textNormal">
                {localization.getText("COMMON_LABEL_SAVE_INTO")}
              </Label>
            </GridLayout.Col>
            <GridLayout.Col cols="9">
              <DropDown
                options={GLOBAL_REPORT}
                onChange={this.onSaveIntoChanged}
                value={saveInto}
                valueField="value"
                labelField="label"
              />
            </GridLayout.Col>
          </GridLayout.Row>
        )}
        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label u_textNormal">
              {localization.getText("COMMON_LABEL_CATEGORY")}
            </Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <Select
              simpleValue
              options={categories}
              labelKey="name"
              valueKey="id"
              value={store.model.categoryId}
              noResultsText={noResultsTextCategory}
              onChange={this.onCategoryChanged}
              onAddNew={onAddNewCategory}
              scalable={false}
            />
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label u_textNormal">
              {localization.getText("REPORTDESIGNER_LABEL_SUB_CATEGORY")}
            </Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <Select
              simpleValue
              options={subCategories}
              labelKey="name"
              valueKey="id"
              value={model.subCategoryId}
              noResultsText={noResultsTextSubCategory}
              onChange={this.onSubCategoryChanged}
              onAddNew={onAddNewSubCategory}
              disabled={store.disableSubCategory}
              scalable={false}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }

  render() {
    const { localization, store, onClosing } = this.props;
    const popupTitle = !store.canSaveAs
      ? localization.getText("COMMON_LABEL_SAVE")
      : localization.getText("COMMON_LABEL_SAVE_AS");

    return (
      <Popup visible onClosing={onClosing} autoClose={false} type="none">
        <Popup.Header title={popupTitle} />
        <Popup.Body>{this.renderContainer()}</Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">
            {localization.getText("COMMON_LABEL_OK")}
          </Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">
            {localization.getText("COMMON_LABEL_CANCEL")}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

export default SavePopup;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/SavePopup/Izenda.SavePopup.js